import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles, Button, CircularProgress } from '@material-ui/core'
import { get, map, head } from 'lodash'
import { adminDatabaseList } from '../actions/admin_database'
import AdminMainLayout from './AdminMainLayout'


export const AdminDatabase = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [lastUpdated, setLastUpdated] = useState(null)
  const stats = head(useSelector(() => adminDatabaseList.getVisibleObjects()))
  const loading = useSelector(() => adminDatabaseList.isLoading())
  const connections = get(stats, 'connections', {})
  const databaseStats = get(stats, 'database_stats', {})
  const activeQueries = get(stats, 'active_queries', [])

  const updateStats = () => {
    dispatch(adminDatabaseList.invalidateList())
    dispatch(adminDatabaseList.fetchListIfNeeded())
    setLastUpdated(new Date())
  }

  useEffect(() => {
    updateStats()
  }, [])

  const getConnectionBarColor = (total, max) => {
    const ratio = total / max
    if (ratio > 0.8) return '#d32f2f'
    if (ratio > 0.5) return '#f57c00'
    return '#388e3c'
  }

  const getCacheHitClass = (ratio) => {
    if (ratio >= 99) return classes.statValueGood
    if (ratio >= 95) return classes.statValueWarning
    return classes.statValueBad
  }

  const getDurationClass = (seconds) => {
    if (seconds > 5) return classes.durationBad
    if (seconds > 1) return classes.durationWarning
    return ''
  }

  const getConnectionStatus = (total, max) => {
    const ratio = total / max
    if (ratio > 0.8) return { class: classes.statusBad, text: 'Critical - connection pool nearly exhausted' }
    if (ratio > 0.5) return { class: classes.statusWarning, text: 'Elevated - monitor closely' }
    return { class: classes.statusGood, text: 'Healthy' }
  }

  const getCacheStatus = (ratio) => {
    if (ratio >= 99) return { class: classes.statusGood, text: 'Excellent - most reads from memory' }
    if (ratio >= 95) return { class: classes.statusWarning, text: 'Acceptable - some disk reads' }
    if (ratio >= 80) return { class: classes.statusBad, text: 'Poor - too many disk reads' }
    return { class: classes.statusBad, text: 'Very low - cache may be cold or undersized' }
  }

  const getActiveQueryStatus = (count) => {
    if (count === 0) return { class: classes.statusGood, text: 'Idle - no queries running' }
    if (count <= 5) return { class: classes.statusGood, text: 'Normal activity' }
    if (count <= 15) return { class: classes.statusWarning, text: 'Busy - multiple concurrent queries' }
    return { class: classes.statusBad, text: 'Heavy load - queries may be backing up' }
  }

  return (
    <AdminMainLayout>
      <div className={classes.container}>
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <h1 className={classes.title}>Database</h1>
            <p className={classes.subtitle}>Database activity and connection monitoring</p>
          {lastUpdated && (
            <p className={classes.lastUpdated}>
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
          )}
        </div>
        <Button
          variant="contained"
          color="primary"
          onClick={updateStats}
          disabled={loading}
          className={classes.refreshButton}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {loading && !stats ? (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      ) : (
        <>
          <div className={classes.section}>
            <h2 className={classes.sectionTitle}>Connections</h2>
            <div className={classes.cardGrid}>
              <div className={classes.card}>
                <div className={classes.cardTitle}>
                  <span className={classes.cardIcon}>C</span>
                  Connection Usage
                </div>
                <div className={classes.cardContent}>
                  <div className={classes.statsRow}>
                    <span className={classes.statLabel}>Total connections</span>
                    <span className={classes.statValue}>
                      {connections.total || 0} / {connections.max_connections || 100}
                    </span>
                  </div>
                  <div className={classes.connectionBar}>
                    <div
                      className={classes.connectionBarFill}
                      style={{
                        width: `${((connections.total || 0) / (connections.max_connections || 100)) * 100}%`,
                        backgroundColor: getConnectionBarColor(connections.total || 0, connections.max_connections || 100),
                      }}
                    />
                  </div>
                  <p className={getConnectionStatus(connections.total || 0, connections.max_connections || 100).class}>
                    {getConnectionStatus(connections.total || 0, connections.max_connections || 100).text}
                  </p>
                  <p className={classes.statusHint}>Target: below 50% usage</p>
                </div>
              </div>

              <div className={classes.card}>
                <div className={classes.cardTitle}>
                  <span className={classes.cardIcon}>S</span>
                  Connection States
                </div>
                <div className={classes.cardContent}>
                  <div className={classes.statsRow}>
                    <span className={classes.statLabel}>Active (running queries)</span>
                    <span className={classes.statValue}>{connections.active || 0}</span>
                  </div>
                  <div className={classes.statsRow}>
                    <span className={classes.statLabel}>Idle</span>
                    <span className={classes.statValue}>{connections.idle || 0}</span>
                  </div>
                  <div className={classes.statsRow}>
                    <span className={classes.statLabel}>Idle in transaction</span>
                    <span className={classes.statValue}>{connections.idle_in_transaction || 0}</span>
                  </div>
                </div>
              </div>

              <div className={classes.card}>
                <div className={classes.cardTitle}>
                  <span className={`${classes.cardIcon} ${databaseStats.cache_hit_ratio >= 99 ? classes.cardIconSuccess : classes.cardIconWarning}`}>%</span>
                  Cache Performance
                </div>
                <div className={classes.cardContent}>
                  <div className={classes.statsRow}>
                    <span className={classes.statLabel}>Cache hit ratio</span>
                    <span className={`${classes.statValue} ${getCacheHitClass(databaseStats.cache_hit_ratio || 0)}`}>
                      {databaseStats.cache_hit_ratio || 0}%
                    </span>
                  </div>
                  <div className={classes.statsRow}>
                    <span className={classes.statLabel}>Blocks from cache</span>
                    <span className={classes.statValue}>
                      {(databaseStats.blocks_hit || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className={classes.statsRow}>
                    <span className={classes.statLabel}>Blocks from disk</span>
                    <span className={classes.statValue}>
                      {(databaseStats.blocks_read || 0).toLocaleString()}
                    </span>
                  </div>
                  <p className={getCacheStatus(databaseStats.cache_hit_ratio || 0).class}>
                    {getCacheStatus(databaseStats.cache_hit_ratio || 0).text}
                  </p>
                  <p className={classes.statusHint}>Target: above 99% in production</p>
                </div>
              </div>
            </div>
          </div>

          <div className={classes.section}>
            <h2 className={classes.sectionTitle}>
              Active Queries ({activeQueries.length})
              <span style={{ marginLeft: '12px', fontWeight: 400, fontSize: '14px' }} className={getActiveQueryStatus(activeQueries.length).class}>
                - {getActiveQueryStatus(activeQueries.length).text}
              </span>
            </h2>
            {activeQueries.length > 0 ? (
              <table className={classes.queryTable}>
                <thead className={classes.queryTableHeader}>
                  <tr>
                    <th>PID</th>
                    <th>Duration</th>
                    <th>State</th>
                    <th>Query</th>
                  </tr>
                </thead>
                <tbody className={classes.queryTableBody}>
                  {map(activeQueries, (query, index) => (
                    <tr key={index}>
                      <td>{query.pid}</td>
                      <td className={getDurationClass(query.duration_seconds || 0)}>
                        {query.duration_seconds ? `${query.duration_seconds.toFixed(2)}s` : '--'}
                      </td>
                      <td>{query.state}</td>
                      <td className={classes.queryText} title={query.query}>
                        {query.query}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className={classes.noQueries}>
                No active queries running
              </div>
            )}
          </div>
        </>
      )}
      </div>
    </AdminMainLayout>
  )
}

const useStyles = makeStyles((theme) => ({
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerLeft: {
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#333',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
  },
  refreshButton: {
    marginTop: '8px',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '20px',
    marginBottom: '24px',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    padding: '20px',
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#333',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  cardIcon: {
    width: '24px',
    height: '24px',
    backgroundColor: '#e3f2fd',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#1976d2',
    fontSize: '14px',
  },
  cardIconWarning: {
    backgroundColor: '#fff3e0',
    color: '#f57c00',
  },
  cardIconSuccess: {
    backgroundColor: '#e8f5e9',
    color: '#388e3c',
  },
  cardContent: {
    fontSize: '14px',
    color: '#666',
    lineHeight: 1.5,
  },
  statsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #eee',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  statLabel: {
    fontSize: '13px',
    color: '#666',
  },
  statValue: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
  },
  statValueGood: {
    color: '#388e3c',
  },
  statValueWarning: {
    color: '#f57c00',
  },
  statValueBad: {
    color: '#d32f2f',
  },
  section: {
    marginBottom: '32px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#333',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: '2px solid #1976d2',
  },
  queryTable: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#fff',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  queryTableHeader: {
    backgroundColor: '#f5f5f5',
    '& th': {
      padding: '12px 16px',
      textAlign: 'left',
      fontSize: '13px',
      fontWeight: 600,
      color: '#666',
      borderBottom: '1px solid #eee',
    },
  },
  queryTableBody: {
    '& td': {
      padding: '12px 16px',
      fontSize: '13px',
      borderBottom: '1px solid #eee',
      verticalAlign: 'top',
    },
    '& tr:last-child td': {
      borderBottom: 'none',
    },
  },
  queryText: {
    maxWidth: '500px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: 'monospace',
    fontSize: '12px',
  },
  durationWarning: {
    color: '#f57c00',
    fontWeight: 600,
  },
  durationBad: {
    color: '#d32f2f',
    fontWeight: 600,
  },
  noQueries: {
    padding: '24px',
    textAlign: 'center',
    color: '#999',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: '40px',
  },
  lastUpdated: {
    fontSize: '12px',
    color: '#999',
    marginTop: '8px',
  },
  connectionBar: {
    height: '8px',
    backgroundColor: '#e0e0e0',
    borderRadius: '4px',
    marginTop: '8px',
    overflow: 'hidden',
  },
  connectionBarFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  statusHint: {
    fontSize: '11px',
    color: '#999',
    marginTop: '8px',
    fontStyle: 'italic',
  },
  statusGood: {
    color: '#388e3c',
    fontWeight: 500,
    fontSize: '12px',
  },
  statusWarning: {
    color: '#f57c00',
    fontWeight: 500,
    fontSize: '12px',
  },
  statusBad: {
    color: '#d32f2f',
    fontWeight: 500,
    fontSize: '12px',
  },
}))
